import { FC, ReactNode, useMemo } from "react";

import {
  NOTIFICATION_ACTION_TYPES,
  createNotificationAction,
} from "shared/ui/notifications/notification-generic/base-parts/actions";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { NotificationBase } from "@/modules/notifications/notification/base";
import {
  DialBlockNotificationI,
  NotificationI,
} from "@/lib/types/notification";
import { useLocalTimezone } from "@/modules/dial-blocks/hooks/use-local-timezone";
import { getZoneLabel } from "@/modules/dial-blocks/lib/display-timezone";
import {
  formatDayAndTime,
  formatDialBlockRange,
  getDialBlockEnd,
  getEtDateKey,
  parseInstant,
} from "@/modules/dial-blocks/lib/format";
import Router from "@/helpers/router";

type NotificationDialBlockChangedPropsI = NotificationI & PropsWithClassNameI;

const ACTOR_LABELS: Record<string, string> = {
  customer: "Your LGA",
  admin: "Glencoco",
  // C-14: an LGA or Admin acting from the user app. The caller can't tell
  // which, so the card says "Your manager" (coordinator decision D4).
  manager: "Your manager",
};

const FALLBACK_TEXT =
  "Your dial blocks were changed. Open My Week to see them.";

const FALLBACK_CAMPAIGN = "a campaign";

/** A cancellation caused by a cut to the campaign's capacity (B2b). */
const CAPACITY_REDUCED = "capacity_reduced";

/** A cancellation because the campaign's blocks were paused (C-16 S8). */
const CAMPAIGN_PAUSED = "campaign_paused";

/** A cancellation because the campaign stopped calling that day (C-16 S8). */
const CALLS_DAY_OFF = "calls_day_off";

/**
 * The sentence for one change to the caller's dial block, in `zone`. A
 * missing or unreadable payload still reads as a change, so the bell never
 * counts a row the list can't show.
 */
export const describeDialBlockChange = (
  change: DialBlockNotificationI | undefined,
  campaignName: string | undefined,
  zone: string
): ReactNode => {
  if (!change) {
    return FALLBACK_TEXT;
  }

  const actor = ACTOR_LABELS[change.changed_by ?? ""] ?? "Someone";
  const from = parseInstant(change.from_start_time);
  const to = parseInstant(change.to_start_time);
  const fromCampaign =
    change.from_campaign_name || campaignName || FALLBACK_CAMPAIGN;
  const toCampaign =
    change.to_campaign_name || campaignName || FALLBACK_CAMPAIGN;

  switch (change.action) {
    case "booked": {
      if (!to) {
        break;
      }

      return (
        <>
          <b>{actor}</b> booked a dial block for you on <b>{toCampaign}</b>:{" "}
          {formatDialBlockRange(to, getDialBlockEnd(to), zone)}{" "}
          {getZoneLabel(zone, to)}.
        </>
      );
    }

    case "cancelled": {
      if (!from) {
        break;
      }

      // Command Center's "Block Cancelled - Capacity Reduced".
      if (change.reason === CAPACITY_REDUCED) {
        return (
          <>
            Your dial block on <b>{fromCampaign}</b>,{" "}
            {formatDayAndTime(from, zone)} {getZoneLabel(zone, from)}, was
            cancelled due to a capacity reduction.
          </>
        );
      }

      if (change.reason === CAMPAIGN_PAUSED) {
        return (
          <>
            Your dial block on <b>{fromCampaign}</b>,{" "}
            {formatDayAndTime(from, zone)} {getZoneLabel(zone, from)}, was
            cancelled because the campaign&apos;s blocks were paused. It
            won&apos;t count against you.
          </>
        );
      }

      if (change.reason === CALLS_DAY_OFF) {
        return (
          <>
            Your dial block on <b>{fromCampaign}</b>,{" "}
            {formatDayAndTime(from, zone)} {getZoneLabel(zone, from)}, was
            cancelled because the campaign no longer calls on that day. It
            won&apos;t count against you.
          </>
        );
      }

      return (
        <>
          <b>{actor}</b> cancelled your dial block on <b>{fromCampaign}</b>:{" "}
          {formatDayAndTime(from, zone)} {getZoneLabel(zone, from)}.
        </>
      );
    }

    case "moved": {
      if (!from || !to) {
        break;
      }

      const timeChanged = from.getTime() !== to.getTime();
      // Ids when both are there; the names written with the change otherwise.
      const campaignChanged =
        change.from_campaign_id && change.to_campaign_id
          ? change.from_campaign_id !== change.to_campaign_id
          : !!change.from_campaign_name &&
            !!change.to_campaign_name &&
            change.from_campaign_name !== change.to_campaign_name;
      const zoneLabel = getZoneLabel(zone, to);
      // A move across a DST change has two offsets in non-US zones (GMT+1 →
      // GMT): then each time carries its own label.
      const fromLabel = getZoneLabel(zone, from);
      const fromTime =
        fromLabel === zoneLabel
          ? formatDayAndTime(from, zone)
          : `${formatDayAndTime(from, zone)} ${fromLabel}`;

      if (timeChanged && campaignChanged) {
        return (
          <>
            <b>{actor}</b> moved your dial block from <b>{fromCampaign}</b>,{" "}
            {fromTime}, to <b>{toCampaign}</b>, {formatDayAndTime(to, zone)}{" "}
            {zoneLabel}.
          </>
        );
      }

      if (campaignChanged) {
        return (
          <>
            <b>{actor}</b> moved your dial block on {formatDayAndTime(to, zone)}{" "}
            {zoneLabel} from <b>{fromCampaign}</b> to <b>{toCampaign}</b>.
          </>
        );
      }

      return (
        <>
          <b>{actor}</b> moved your dial block on <b>{toCampaign}</b> from{" "}
          {fromTime} to {formatDayAndTime(to, zone)} {zoneLabel}.
        </>
      );
    }
  }

  return FALLBACK_TEXT;
};

/** The ET date My Week should open on: the block's new time, else its old one. */
export const getDialBlockChangeWeek = (
  change: DialBlockNotificationI | undefined
): string | undefined => {
  const at =
    parseInstant(change?.to_start_time) ??
    parseInstant(change?.from_start_time);

  return at ? getEtDateKey(at) : undefined;
};

/**
 * Someone other than the caller (their LGA or Glencoco staff) booked, moved
 * or cancelled one of their dial blocks (GLE-8273). Opens My Week on the
 * affected week.
 */
export const NotificationDialBlockChanged: FC<
  NotificationDialBlockChangedPropsI
> = (props) => {
  const { id, is_seen, created_at, className, campaign_name, dial_block } =
    props;

  const { zone } = useLocalTimezone();

  const text = useMemo(
    () => describeDialBlockChange(dial_block, campaign_name, zone),
    [dial_block, campaign_name, zone]
  );

  const week = useMemo(() => getDialBlockChangeWeek(dial_block), [dial_block]);

  return (
    <NotificationBase
      id={id}
      isUnread={!is_seen}
      className={className}
      avatar={props}
      description={{
        text: <>{text}</>,
        date: created_at,
        campaignName: campaign_name,
      }}
      actions={[
        createNotificationAction(
          NOTIFICATION_ACTION_TYPES.PRIMARY,
          "View my week",
          () => {
            window.location.href = Router.myWeek(week);
          }
        ),
      ]}
    />
  );
};
